function toNumber(value, fallback = 0) {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const MAX_VIEWBOX_EXPANSION = 4

function getDeclaredArtboard(svgNode) {
  const background = svgNode.querySelector('[data-lugsail-theme-background="true"]')
  if (!background) return null

  const x = toNumber(background.getAttribute('x'))
  const y = toNumber(background.getAttribute('y'))
  const width = toNumber(
    background.dataset.lugsailBaseWidth || background.getAttribute('width')
  )
  const height = toNumber(
    background.dataset.lugsailBaseHeight || background.getAttribute('height')
  )

  return width > 0 && height > 0 ? { x, y, width, height } : null
}

function getBBoxMetrics(svgNode) {
  if (!svgNode?.getBBox) return null

  try {
    const bbox = svgNode.getBBox()
    if (!Number.isFinite(bbox?.width) || !Number.isFinite(bbox?.height)) {
      return null
    }
    return bbox
  } catch (error) {
    return null
  }
}

function temporarilyExcludeNodes(svgNode, selectors = []) {
  const removed = []

  selectors
    .filter(Boolean)
    .forEach((selector) => {
      Array.from(svgNode.querySelectorAll(selector)).forEach((node) => {
        const parent = node.parentNode
        if (!parent) return
        removed.push({
          node,
          parent,
          nextSibling: node.nextSibling,
        })
        parent.removeChild(node)
      })
    })

  return () => {
    removed.forEach(({ node, parent, nextSibling }) => {
      if (nextSibling && nextSibling.parentNode === parent) {
        parent.insertBefore(node, nextSibling)
      } else {
        parent.appendChild(node)
      }
    })
  }
}

export function getSvgMetrics(svgNode, options = {}) {
  const restoreNodes = temporarilyExcludeNodes(
    svgNode,
    options.excludeSelectors || []
  )

  try {
    const viewBox = svgNode.getAttribute('viewBox')
    const bbox = getBBoxMetrics(svgNode)

    if (viewBox) {
      const [x = 0, y = 0, width = 0, height = 0] = viewBox
        .split(/\s+/)
        .map((value) => Number.parseFloat(value))

      if (bbox) {
        const minX = Math.min(x, bbox.x)
        const minY = Math.min(y, bbox.y)
        const maxX = Math.max(x + width, bbox.x + bbox.width)
        const maxY = Math.max(y + height, bbox.y + bbox.height)
        return {
          x: minX,
          y: minY,
          width: maxX - minX,
          height: maxY - minY,
          hasViewBox: true,
        }
      }

      return {
        x,
        y,
        width,
        height,
        hasViewBox: true,
      }
    }

    const width =
      toNumber(svgNode.getAttribute('width')) ||
      svgNode.clientWidth ||
      toNumber(svgNode.style.width)
    const height =
      toNumber(svgNode.getAttribute('height')) ||
      svgNode.clientHeight ||
      toNumber(svgNode.style.height)

    if (bbox) {
      const minX = Math.min(0, bbox.x)
      const minY = Math.min(0, bbox.y)
      const maxX = Math.max(width, bbox.x + bbox.width)
      const maxY = Math.max(height, bbox.y + bbox.height)
      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        hasViewBox: false,
      }
    }

    return {
      x: 0,
      y: 0,
      width,
      height,
      hasViewBox: false,
    }
  } finally {
    restoreNodes()
  }
}

export function fitSvgViewBoxToContents(svgNode) {
  if (!svgNode) return

  // Exclude axis titles from the main bounding-box pass. They are positioned
  // after the chart renderer has calculated its artboard, so account for
  // their overflow explicitly below rather than allowing their old placement
  // to distort the chart bounds.
  const metrics = getSvgMetrics(svgNode, {
    excludeSelectors: [
      '[data-lugsail-axis-guide-line="true"]',
      '[data-lugsail-axis-title]',
    ],
  })
  if (!Number.isFinite(metrics.width) || !Number.isFinite(metrics.height)) {
    return
  }

  const declaredArtboard = getDeclaredArtboard(svgNode)
  // Measurements can be corrupted by an off-artboard SVG node (for example a
  // transformed axis label). Do not let that one node turn a normal chart into
  // a multi-million-pixel SVG. This guard is shared by preview and export.
  if (
    declaredArtboard &&
    (metrics.width > declaredArtboard.width * MAX_VIEWBOX_EXPANSION ||
      metrics.height > declaredArtboard.height * MAX_VIEWBOX_EXPANSION)
  ) {
    svgNode.setAttribute(
      'viewBox',
      `${declaredArtboard.x} ${declaredArtboard.y} ${declaredArtboard.width} ${declaredArtboard.height}`
    )
    svgNode.setAttribute('width', String(declaredArtboard.width))
    svgNode.setAttribute('height', String(declaredArtboard.height))
    return
  }

  // Clamp left edge to the brand-theme background rect so long axis labels
  // don't expand the viewBox far left and shrink the visible chart.
  const bgRect = svgNode.querySelector('[data-lugsail-theme-background="true"]')
  const bgLeft = bgRect ? parseFloat(bgRect.getAttribute('x') || '0') : -Infinity
  let viewX = Number.isFinite(bgLeft) ? Math.max(metrics.x, bgLeft) : metrics.x
  let viewW = metrics.width - (viewX - metrics.x)

  // Expand the bottom edge if any x-axis title renders below the current boundary.
  // getBBox() on an unrotated text node is reliable for its bottom extent.
  let viewH = metrics.height
  svgNode.querySelectorAll('[data-lugsail-axis-title="x"]').forEach((titleNode) => {
    try {
      const bbox = titleNode.getBBox()
      const titleBottom = bbox.y + bbox.height - metrics.y
      if (titleBottom > viewH) viewH = titleBottom
    } catch { /* node not in layout */ }
  })

  // A rotated y-axis title can legitimately extend beyond the original left
  // artboard edge. Unlike long tick labels, it is an intentional label, so
  // retain it in the viewport instead of clipping its first characters.
  svgNode.querySelectorAll('[data-lugsail-axis-title="y"]').forEach((titleNode) => {
    try {
      const bbox = titleNode.getBBox()
      if (!Number.isFinite(bbox.x) || !Number.isFinite(bbox.width)) return
      const titleLeft = bbox.x
      if (titleLeft < viewX) {
        viewW += viewX - titleLeft
        viewX = titleLeft
      }
    } catch { /* node not in layout */ }
  })

  svgNode.setAttribute('viewBox', `${viewX} ${metrics.y} ${viewW} ${viewH}`)
  svgNode.setAttribute('width', String(viewW))
  svgNode.setAttribute('height', String(viewH))
}
